import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useApi } from '../hooks/useApi';
import { useURLState } from '../hooks/useURLState';
import Navigation from "../components/Navigation";
import AssessmentStatusBadge from "../utils/assessmentStatusLabel";
import Modal from '../components/Modal';
import Card from '../components/Card';
import Button from '../components/Buttons';
import Input from '../components/Input';
import { useApplicationTypes } from "../contexts/ApplicationTypeContext";
import { useUserBases } from "../contexts/UserBaseContext";
import { useDeploymentModels } from "../contexts/DeploymentModelContext";
import LoadingSpinner from '../components/LoadingSpinner';
import { severityPostureScore } from '../utils/riskScoringEngine';
import { Plus, FileText, Shield, Loader, AlertCircle, Trash2, RefreshCw, Pencil, Lock, Unlock, Users, Eye } from 'lucide-react';
import Pagination from "../components/Pagination";
import { usePageSize } from "../hooks/usePageSize";
import { useRegions } from "../contexts/RegionContext";
import RegionField from "./shared/RegionField";
import { useVerticals } from "../contexts/VerticalsContext";
import AssignAssessModal from "../components/AssignAssessModal";
import AssessmentRespondentsModal from "../components/AssessmentRespondentsModal";
import AssessmentUnassignedModal from "../components/AssessmentUnassignedModal";
import TPRMNewVendorModal from "./TPRMNewVendorModal";
import CreateDealModal from "./CreateDealModal";
import DealShareModal from "./DealShareModal";
import DeleteAssessmentModal from "./DeleteAssessmentModal";
import ReassessModal from './ReassessModal';
import { useRBAC } from "../hooks/useRBAC";
import { useHasLicense } from "../contexts/LicenseContext";
import AssessmentActivityLog from "../components/AssessmentActivityLog";
import IntelligenceNarrative from "./IntelligenceNarrative";

const deal = () => {
  const [deals, setdeals] = useState([]);
  const [applications, setApplications] = useState([]);
  const [applicationsWithRisks, setApplicationsWithRisks] = useState([]);
  const [selectedDealId, setSelectedDealId] = useURLState('dealId');
  const [currentPageParam, setCurrentPageParam] = useURLState('page');
  const currentPage = parseInt(currentPageParam) || 1;
  const selectedDeal = deals.find(d => String(d.dealID) === selectedDealId) || null;
  const [showNewDealModal, setShowNewDealModal] = useState(false);
  const [showNewApplicationModal, setShowNewApplicationModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [applicationsLoading, setApplicationsLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [paging, setPaging] = useState(false);
  const [pageSize, setPageSize] = usePageSize('deals');
  const listTopRef = useRef(null);
  const applicationTypes = useApplicationTypes() || [];
  const userBases = useUserBases() || [];
  const deploymentModels = useDeploymentModels() || [];

  const navigate = useNavigate();
  const location = useLocation();
  const apiCall = useApi();
  const { user, refreshUserRoles, isSuperAdmin, isSupportAdmin } = useAuth();

  const regions = useRegions();
  const [formData, setFormData] = useState({
    regionId: '',
    vendorId: '',
    appScope: '',
  });

  const { verticals, subVerticals } = useVerticals();
  const { canAssess, getAssessDisabledReason, hasMinimumRoleAtOrAbove, canManageOrganizationRisks, canViewDeal, canAssessViaAssignment, canCreateDealOrBusinessUnit, canDeleteAssessments, isDealOwner, isDealAdmin, authVersion } = useRBAC();
  const selectedOrg = JSON.parse(localStorage.getItem("selectedOrganization") || "{}");
  const canManageRisks = canManageOrganizationRisks();
  const canManageAppsForSelectedDeal = canManageRisks ||
    (selectedDeal && hasMinimumRoleAtOrAbove('ADMIN', 'DEAL', selectedDeal?.dealID));
  const canAddDealOrBU = canCreateDealOrBusinessUnit(selectedOrg?.organizationId);
  const canDeleteAssessment = canDeleteAssessments(); // super admin only
  const hasLicense = useHasLicense();
  // const [selectedVerticalId, setSelectedVerticalId] = useState(null);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [respondentsAssessmentId, setRespondentsAssessmentId] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [assessmentToDelete, setAssessmentToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [showReassessModal, setShowReassessModal] = useState(false);
  const [assessmentToReassess, setAssessmentToReassess] = useState(null);
  const [reassessing, setReassessing] = useState(false);
  const [showUnassignedAlert, setShowUnassignedAlert] = useState(false);
  const [showEditApplicationModal, setShowEditApplicationModal] = useState(false);
  const [editingApplication, setEditingApplication] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const rightPanelRef = useRef(null);
  const fetchIdRef = useRef(0);
  // Tracks the dealId we've already attempted to page-locate, so we only walk
  // pages once per selection (and clear if the user later pages away from it).
  const locateAttemptedForRef = useRef(null);

  // Privacy / sharing state for the selected deal. The paged summary rows don't carry
  // privacy/ownership fields, so the full DealDto is fetched per selection.
  const [dealDetail, setDealDetail] = useState(null);
  const [privacyUpdating, setPrivacyUpdating] = useState(false);
  const [privacyNotice, setPrivacyNotice] = useState(null);
  const [showShareModal, setShowShareModal] = useState(false);

  // Vendor state
  const [vendors, setVendors] = useState([]);
  const [vendorsLoading, setVendorsLoading] = useState(false);
  const [showNewVendorModal, setShowNewVendorModal] = useState(false);

  // Reset application state when user/auth changes. Skip the initial mount so we don't
  // clobber a dealId arriving in the URL (e.g. from a "Recent Deals" dashboard click).
  const prevAuthVersionRef = useRef(authVersion);
  useEffect(() => {
    if (prevAuthVersionRef.current === authVersion) return;
    prevAuthVersionRef.current = authVersion;
    setApplicationsWithRisks([]);
    setSelectedDealId(null);
  }, [authVersion]);

  useEffect(() => {
    fetchdeals();
    // Vendors are lazy-loaded when modal opens
  }, [currentPage, pageSize, authVersion]);

  // Fetch vendors for the selected organization
  const fetchVendors = async () => {
    const org = JSON.parse(localStorage.getItem("selectedOrganization"));
    if (!org?.organizationId) return;

    setVendorsLoading(true);
    try {
      const data = await apiCall("GET", `/TPRMVendor/vendors?pageSize=100`);
      setVendors(data.items || []);
    } catch (err) {
      setVendors([]);
    } finally {
      setVendorsLoading(false);
    }
  };

  // Handle vendor creation callback
  const handleVendorCreated = async (newVendor) => {
    setShowNewVendorModal(false);
    await fetchVendors();
    // Auto-select the newly created vendor
    setFormData(prev => ({ ...prev, vendorId: newVendor.vendorId?.toString() }));
  };


  const refreshApplications = async () => {
    if (!selectedDeal) return;
    fetchApplications(selectedDeal.dealID);
  };

  const handleDeleteAssessment = async () => {
    if (!assessmentToDelete) return;
    setDeleting(true);
    try {
      await apiCall('DELETE', `/assessments/${assessmentToDelete}`);
      setShowDeleteModal(false);
      setAssessmentToDelete(null);
      await fetchApplications(selectedDeal.dealID);
    } catch (error) {} finally {
      setDeleting(false);
    }
  };

  const handleReassessAssessment = async () => {
    if (!assessmentToReassess) return;
    setReassessing(true);
    try {
      await apiCall('POST', `/assessments/${assessmentToReassess}/reassess`);
      setShowReassessModal(false);
      setAssessmentToReassess(null);
      navigate(`/assessment/${assessmentToReassess}?step=review`);
    } catch (error) {} finally {
      setReassessing(false);
    }
  };

  // Auto-select from navigation state (e.g., returning from assessment)
  useEffect(() => {
    const autoSelectId = location.state?.selecteddealID;
    if (autoSelectId && deals.length > 0) {
      const target = deals.find(a => a.id === autoSelectId);
      if (target) {
        setSelectedDealId(target.id);
      }
      // Clear location.state so it doesn't re-trigger
      navigate('/deals', { replace: true });
    }
  }, [deals]);

  // Auto-fetch applications when URL has dealId and deals are loaded (bookmark/refresh,
  // or arriving from a "Recent Deals" click on the dashboard). If the deal isn't on the
  // current page, page through the list once to find and jump to its page.
  useEffect(() => {
    if (!selectedDealId || deals.length === 0) return;
    const foundInPage = deals.find(d => String(d.dealID) === selectedDealId);
    if (foundInPage) {
      locateAttemptedForRef.current = selectedDealId;
      fetchApplications(foundInPage.dealID);
    } else if (locateAttemptedForRef.current !== selectedDealId) {
      // First time we notice this deal isn't on the current page — try to page to it.
      locateAttemptedForRef.current = selectedDealId;
      locateDealPage(selectedDealId);
    } else {
      // Already located (or the user paged away) — clear the stale selection.
      setSelectedDealId(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDealId, deals]);

  // Load the full DealDto (isPrivate, createdByUserId) for the selected deal
  useEffect(() => {
    if (!selectedDealId) {
      setDealDetail(null);
      setPrivacyNotice(null);
      return;
    }
    setPrivacyNotice(null);
    let cancelled = false;
    const fetchDealDetail = async () => {
      try {
        const dto = await apiCall("GET", `/deals/${selectedDealId}`);
        if (!cancelled) setDealDetail(dto);
      } catch (error) {
        if (!cancelled) setDealDetail(null);
      }
    };
    fetchDealDetail();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDealId]);

  // Walk the deal list (using the server's own paging) to find which page a
  // selected deal lives on, then navigate to that page.
  const locateDealPage = async (dealId) => {
    try {
      let p = 1;
      // Bound the walk by the server's true total count as pages are fetched.
      while (true) {
        const data = await apiCall("GET", `/deals/summary?page=${p}&pageSize=${pageSize}`);
        const items = data.items || [];
        if (items.length === 0) break; // past the last page
        if (items.some(d => String(d.dealId) === String(dealId))) {
          setCurrentPageParam(p); // effect re-runs after fetchdeals reloads this page
          return;
        }
        if (data.totalCount && p * pageSize >= data.totalCount) break;
        p++;
      }
      // Not found on any page — clear the stale selection.
      setSelectedDealId(null);
    } catch (e) {
      setSelectedDealId(null);
    }
  };

  const fetchdeals = async () => {
    setPaging(true);
    try {
      const org = JSON.parse(localStorage.getItem("selectedOrganization"));
      if (!org?.organizationId) {
        return;
      }

      // Use the optimized summary endpoint with paging
      const data = await apiCall("GET", `/deals/summary?page=${currentPage}&pageSize=${pageSize}`);

      // Map the response to match the existing component's expected shape
      const dealsWithCountsAndRisk = (data.items || []).map((deal) => ({
        dealID: deal.dealId,
        isPrivate: deal.isPrivate,
        dealName: deal.dealName,
        verticalId: deal.verticalId,
        domain: deal.domain,
        regionId: deal.regionId,
        targetEndDate: deal.targetEndDate,
        applicationCount: deal.applicationCount,
        riskLevel: deal.riskLevel === "NONE" ? "No RISK" : deal.riskLevel,
        riskCounts: {
          CRITICAL: deal.riskCounts?.critical || 0,
          HIGH: deal.riskCounts?.high || 0,
          MEDIUM: deal.riskCounts?.medium || 0,
          LOW: deal.riskCounts?.low || 0,
        },
      }));

      // Backend /deals/summary already applies RBAC deal-scoping and returns a scoped
      // totalCount, so trust the server response directly. Re-filtering client-side broke
      // pagination for deal-scoped users by clobbering totalCount with the current page's length.
      setdeals(dealsWithCountsAndRisk);
      setTotalCount(data.totalCount || 0);

    } catch (error) {} finally {
      setLoading(false);
      setPaging(false);
    }
  };


  const calculateWeightedDealRiskScore = (appsWithRisks) => {
    const totalScore = appsWithRisks.reduce((sum, app) => sum + app.riskScore, 0);
    const avgScore = appsWithRisks.length > 0 ? Math.round(totalScore / appsWithRisks.length) : 0;

    if (avgScore >= 90) return "CRITICAL";
    if (avgScore >= 75) return "HIGH";
    if (avgScore >= 50) return "MEDIUM";
    if (avgScore >= 1) return "LOW";
    return "No RISK";
  };

  const fetchApplications = async (dealID) => {
    const thisFetchId = ++fetchIdRef.current;
    setApplicationsLoading(true);
    try {
      const summaries = await apiCall("GET", `/assessments/summary?dealId=${dealID}`);
      if (thisFetchId !== fetchIdRef.current) return;

      const appsWithRisks = summaries.map((s) => ({
        applicationId: s.applicationId,
        applicationName: s.applicationName,
        description: s.applicationDescription,
        targetEndDate: s.targetEndDate,
        applicationTypeId: s.applicationTypeId,
        regionId: s.regionId,
        verticalId: s.verticalId,
        subVerticalId: s.subVerticalId,
        userBaseId: s.userBaseId,
        deploymentModelId: s.deploymentModelId,
        applicationOwner: s.applicationOwner,
        criticality: s.criticality,
        appScope: s.appScope,
        vendorId: s.vendorId,
        detailConfirmationPending: s.detailConfirmationPending,
        assignedToUserId: s.assignedToUserId,
        assignedToUserName: s.assignedToUserName,
        assigneeUserIds: s.assigneeUserIds,
        hasAssessment: s.hasAssessment,
        assessmentID: s.assessmentID,
        latestAssessment: s.hasAssessment ? { status: s.status } : null,
        riskCounts: {
          CRITICAL: s.riskCounts?.critical || 0,
          HIGH: s.riskCounts?.high || 0,
          MEDIUM: s.riskCounts?.medium || 0,
          LOW: s.riskCounts?.low || 0,
        },
        totalRisks: s.totalRisks,
        activeRisks: s.activeRisks,
        riskScore: s.riskScore,
      }));

      setApplicationsWithRisks(appsWithRisks);
    } catch (error) {
      if (thisFetchId !== fetchIdRef.current) return;
    } finally {
      if (thisFetchId === fetchIdRef.current) {
        setApplicationsLoading(false);
      }
    }
  };




  const calculateRiskScore = (riskCounts) => severityPostureScore(riskCounts);

  const totalPages = Math.ceil(totalCount / pageSize);

  // Handle deal creation callback
  const handleDealCreated = () => {
    setCurrentPageParam(1);
    fetchdeals();
  };

  // Break-glass mirrors the backend: super/support admins may administer any deal they can see.
  const isPlatformAdmin = !!isSuperAdmin || !!isSupportAdmin;
  const isSelectedDealCreator = !!dealDetail &&
    dealDetail.createdByUserId != null && dealDetail.createdByUserId === user?.id;

  // Privacy toggle — mirrors DealsController.UpdateDealPrivacy exactly:
  // OWNER@DEAL, the deal's creator, or a super/support admin.
  const canTogglePrivacy = !!selectedDeal && !!dealDetail &&
    (isDealOwner(selectedDeal.dealID) || isSelectedDealCreator || isPlatformAdmin);

  // Manage access — mirrors ControllerPermissionExtensions.CanAdministerDealRoles:
  // ADMIN+@DEAL (i.e. OWNER or ADMIN) or a super/support admin. The creator is included
  // because creating a private deal always auto-grants them OWNER@DEAL (DealsController).
  const canManageDealAccess = !!selectedDeal && !!dealDetail &&
    (isDealAdmin(selectedDeal.dealID) || isSelectedDealCreator || isPlatformAdmin);

  const handleTogglePrivacy = async () => {
    if (!selectedDeal || !dealDetail || privacyUpdating) return;
    const makePrivate = !dealDetail.isPrivate;
    setPrivacyUpdating(true);
    try {
      const res = await apiCall("PUT", `/deals/${selectedDeal.dealID}/privacy`, { isPrivate: makePrivate });
      setDealDetail(prev => (prev ? { ...prev, isPrivate: res.isPrivate } : prev));
      setdeals(prev => prev.map(d => d.dealID === selectedDeal.dealID ? { ...d, isPrivate: res.isPrivate } : d));
      if (makePrivate && res.grantedAccessCount > 0) {
        setPrivacyNotice(`${res.grantedAccessCount} user(s) with work already assigned on this deal were given Contributor access so they keep seeing it. Remove them under Manage access if that isn't intended.`);
      } else {
        setPrivacyNotice(null);
      }
      // Making a deal private auto-grants the toggler OWNER@DEAL — refresh cached roles
      await refreshUserRoles();
    } catch (error) {} finally {
      setPrivacyUpdating(false);
    }
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div className="min-h-screen bg-theme-primary">
      <Navigation />
      <div className="max-w-7xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-content-primary">Deal Management</h1>
          <p className="mt-2 text-gray-600">
            Manage Deals and Applications for security risk assessment
          </p>
        </div>

        {/* Intelligence Narrative — RISK_MANAGER+ at ORG only */}
        {canManageRisks && (
          <IntelligenceNarrative
            type="application"
            title="Intelligence Narrative"
            subtitle="AI-generated briefing based on deal and application risks"
            accentColor="blue"
          />
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Deals */}
          <Card>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold">Deals</h2>
                {canAddDealOrBU && (
                  <Button
                    size="sm"
                    onClick={() => setShowNewDealModal(true)}
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    New Deal
                  </Button>
                )}
            </div>

            <div className="space-y-3" ref={listTopRef}>
              {deals.map((deal) => (
                <div
                  key={deal.dealID}
                  className={`p-4 border-2 rounded-lg cursor-pointer transition-colors ${selectedDeal?.dealID === deal.dealID
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300'
                    }`}
                  onClick={() => {
                    setSelectedDealId(deal.dealID);
                    rightPanelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }}
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <h3 className="font-medium text-content-primary">
                        {deal.dealName}
                        {deal.isPrivate && (
                          <span className="inline-flex items-center gap-1 ml-2 px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded align-middle">
                            <Lock className="h-3 w-3" />
                            Private
                          </span>
                        )}
                      </h3>
                      <p className="text-sm text-gray-600">{verticals.find(v => v.verticalId === deal.verticalId)?.verticalName || "N/A"}</p>
                      {deal.domain && (
                        <p className="text-sm text-gray-500 mt-1">{deal.domain}</p>
                      )}
                    </div>
                    <div className="ml-4 text-right">
                      <div className="flex items-center justify-end space-x-2">
                        <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded">
                          {deal.applicationCount || 0} Apps
                        </span>
                      </div>
                      {deal.targetEndDate && (
                        <div className="text-xs text-gray-500 mt-1">
                          Due: {new Date(deal.targetEndDate).toLocaleDateString()}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Risk Bar */}
                  <div className="mt-3">
                    <div className="flex justify-between items-center mb-1">
                      <span className="text-xs text-gray-600">Risk Level</span>
                      <span className="text-xs font-medium text-content-primary">
                        {deal.riskLevel || 'No RISK'}
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className={`h-2 rounded-full ${deal.riskLevel === 'CRITICAL'
                            ? 'bg-red-700'
                            : deal.riskLevel === 'HIGH'
                              ? 'bg-orange-500'
                              : deal.riskLevel === 'MEDIUM'
                                ? 'bg-yellow-500'
                                : deal.riskLevel === 'LOW'
                                  ? 'bg-green-500'
                                  : 'bg-gray-400'
                          }`}
                        style={{
                          width: `${deal.riskLevel === 'CRITICAL'
                              ? 90
                              : deal.riskLevel === 'HIGH'
                                ? 75
                                : deal.riskLevel === 'MEDIUM'
                                  ? 50
                                  : deal.riskLevel === 'LOW'
                                    ? 25
                                    : 0 // ✅ zero width for "No RISK"
                            }%`,
                        }}
                      ></div>
                    </div>
                  </div>


                </div>
              ))}
            </div>

            {/* Pagination Controls */}
            <Pagination
              page={currentPage}
              totalPages={totalPages}
              totalItems={totalCount}
              pageSize={pageSize}
              onPageChange={setCurrentPageParam}
              onPageSizeChange={setPageSize}
              scrollTargetRef={listTopRef}
              busy={paging}
            />
          </Card>

          {/* Applications */}
          <Card ref={rightPanelRef}>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold">Applications</h2>
              {selectedDeal && (
                <div className="flex space-x-2">
                  {canManageAppsForSelectedDeal && hasLicense('Applications') && (
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => {
                        if (vendors.length === 0 && !vendorsLoading) {
                          fetchVendors();
                        }
                        setShowNewApplicationModal(true);
                      }}
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      New Application
                    </Button>
                  )}


                </div>
              )}
            </div>

            {/* Deal privacy / access controls */}
            {selectedDeal && dealDetail && (
              <div className="mb-4 p-3 border border-gray-200 rounded-lg">
                <div className="flex justify-between items-center flex-wrap gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <h3 className="font-medium text-content-primary truncate">{selectedDeal.dealName}</h3>
                    {dealDetail.isPrivate && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded shrink-0">
                        <Lock className="h-3 w-3" />
                        Private
                      </span>
                    )}
                  </div>
                  {(canTogglePrivacy || canManageDealAccess) && (
                    <div className="flex space-x-2 shrink-0">
                      {canTogglePrivacy && (
                        <Button
                          size="sm"
                          variant="secondary"
                          disabled={privacyUpdating}
                          onClick={handleTogglePrivacy}
                        >
                          {privacyUpdating ? (
                            <Loader className="h-3 w-3 mr-1 animate-spin" />
                          ) : dealDetail.isPrivate ? (
                            <Unlock className="h-3 w-3 mr-1" />
                          ) : (
                            <Lock className="h-3 w-3 mr-1" />
                          )}
                          {dealDetail.isPrivate ? "Make public" : "Make private"}
                        </Button>
                      )}
                      {canManageDealAccess && (
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => setShowShareModal(true)}
                        >
                          <Users className="h-3 w-3 mr-1" />
                          Manage access
                        </Button>
                      )}
                    </div>
                  )}
                </div>
                {privacyNotice && (
                  <div className="flex items-start mt-2 p-2 bg-amber-50 border border-amber-200 rounded-md">
                    <AlertCircle className="h-4 w-4 mr-1.5 text-amber-600 shrink-0" />
                    <p className="text-xs text-amber-700">{privacyNotice}</p>
                  </div>
                )}
              </div>
            )}

            {selectedDeal ? (
              applicationsLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
              ) : applicationsWithRisks.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  No applications associated with this deal.
                </div>
              ) : (
              <div className="space-y-3">
                {[...applicationsWithRisks].sort((a, b) =>
                  (a.applicationName || '').localeCompare(b.applicationName || '')
                ).map((application) => (
                  <div
                    key={application.applicationId}
                    className="p-4 border border-gray-200 rounded-lg hover:border-gray-300"
                  >
                    {/* Title and description row */}
                    <div>
                      <div className="flex items-center gap-1.5">
                        <h3 className="font-medium text-content-primary">{application.applicationName}<AssessmentStatusBadge status={application.latestAssessment?.status} /></h3>
                        {canManageAppsForSelectedDeal && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              const canEditAll = !application.hasAssessment || application.detailConfirmationPending;
                              setEditingApplication(application);
                              setEditFormData({
                                applicationName: application.applicationName || '',
                                applicationOwner: application.applicationOwner || '',
                                targetEndDate: application.targetEndDate ? new Date(application.targetEndDate).toISOString().split('T')[0] : '',
                                criticality: application.criticality || 'MEDIUM',
                                applicationDescription: application.description || '',
                                appScope: application.appScope || '',
                                vendorId: application.vendorId?.toString() || '',
                                verticalId: application.verticalId || '',
                                subVerticalId: application.subVerticalId || '',
                                applicationTypeId: application.applicationTypeId?.toString() || '',
                                userBaseId: application.userBaseId?.toString() || '',
                                deploymentModelId: application.deploymentModelId?.toString() || '',
                                canEditAll,
                              });
                              setShowEditApplicationModal(true);
                              if (vendors.length === 0) fetchVendors();
                            }}
                            className="text-gray-400 hover:text-blue-600 transition-colors"
                            title="Edit application"
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </button>
                        )}
                      </div>
                      {application.description && (
                        <p className="text-sm text-gray-500 mt-1">{application.description}</p>
                      )}
                      {application.targetEndDate && (
                        <p className="text-xs text-gray-500 mt-1">
                          Due: {new Date(application.targetEndDate).toLocaleDateString()}
                        </p>
                      )}
                      {application.assignedToUserName && (
                        <p className="text-xs text-blue-600 mt-1">
                          Assigned to: <span className="font-semibold">{application.assignedToUserName}</span>
                        </p>
                      )}
                      {/* Status badges for analyzing/analysis_failed */}
                      {application.latestAssessment?.status === "analyzing" && (
                        <div className="flex items-center mt-2">
                          <span className="inline-flex items-center px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                            <Loader className="h-3 w-3 mr-1 animate-spin" />
                            Analyzing...
                          </span>
                        </div>
                      )}
                      {application.latestAssessment?.status === "analysis_failed" && (
                        <div className="flex items-center mt-2">
                          <span className="inline-flex items-center px-2 py-1 bg-red-100 text-red-800 text-xs rounded-full">
                            <AlertCircle className="h-3 w-3 mr-1" />
                            Analysis Failed
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Actions row */}
                    <div className="flex flex-wrap gap-2 mt-3">
                      {canManageAppsForSelectedDeal && (
                        <Button
                          size="sm"
                          variant="secondary"
                          disabled={
                            application.hasAssessment &&
                            (application.latestAssessment?.status === "completed" ||
                             application.latestAssessment?.status === "analyzing")
                          }
                          title={
                            application.hasAssessment && application.latestAssessment?.status === "completed"
                              ? "Assessment is completed"
                              : application.hasAssessment && application.latestAssessment?.status === "analyzing"
                                ? "Analysis in progress"
                                : ""
                          }
                          onClick={() => {
                            setSelectedApplication(application);
                            setShowAssignModal(true);
                          }}
                        >
                          <Shield className="h-3 w-3 mr-1" />
                          {application.assignedToUserName ? "Reassign" : "Assign"}
                        </Button>
                      )}

                      {canManageAppsForSelectedDeal && application.assessmentID && (
                        <Button
                          size="sm"
                          variant="secondary"
                          className="whitespace-nowrap"
                          title="Manage additional respondents for this assessment"
                          onClick={() => setRespondentsAssessmentId(application.assessmentID)}
                        >
                          <Users className="h-4 w-4 mr-1" />
                          Respondents
                        </Button>
                      )}

                      <Button
                        size="sm"
                        variant="secondary"
                        disabled={
                          !canAssess(application.assignedToUserId, null, 'DEAL', selectedDeal?.dealID, application.assigneeUserIds || []) ||
                          (application.hasAssessment &&
                            (application.latestAssessment?.status === "completed" ||
                             application.latestAssessment?.status === "analyzing"))
                        }
                        title={
                          (application.hasAssessment && application.latestAssessment?.status === "completed"
                              ? "Assessment is completed"
                              : application.hasAssessment && application.latestAssessment?.status === "analyzing"
                                ? "Analysis in progress"
                                : "")
                            || getAssessDisabledReason(application.assignedToUserId, null, 'DEAL', selectedDeal?.dealID, application.assigneeUserIds || [])
                            || ""
                        }
                        onClick={() => {
                          if (!application.assignedToUserId) {
                            setShowUnassignedAlert(true);
                            return;
                          }

                          try {
                            const assessPath = application.assessmentID
                              ? `/assessment/${application.assessmentID}`
                              : '/assessment';
                            navigate(assessPath, {
                              state: {
                                applicationId: application.applicationId,
                                applicationName: application.applicationName,
                                applicationDescription: application.description,
                                applicationTypeId: application.applicationTypeId,
                                regionId: application.regionId,
                                verticalId: application.verticalId,
                                subVerticalId: application.subVerticalId,
                                userBaseId: application.userBaseId,
                                deploymentModelId: application.deploymentModelId,
                                dealID: selectedDeal.dealID,
                                dealName: selectedDeal.dealName
                              }
                            });
                          } catch (error) {}
                        }}
                      >
                        <FileText className="h-3 w-3 mr-1" />
                        Assess
                      </Button>

                      {/* Viewing mirrors the backend's viewable-assessment rule: deal scope or assignment. */}
                      {application.hasAssessment && application.assessmentID && application.latestAssessment?.status === "completed" &&
                        (canViewDeal(selectedDeal?.dealID) || canAssessViaAssignment(application.assignedToUserId, application.assigneeUserIds || [])) && (
                        <Button
                          size="sm"
                          variant="secondary"
                          title="View the completed assessment's questions, answers, notes, and attachments (read-only)"
                          onClick={() => navigate(`/assessment/${application.assessmentID}?step=review`)}
                        >
                          <Eye className="h-4 w-4 mr-1" />
                          View Answers
                        </Button>
                      )}

                      {application.latestAssessment?.status === "analyzing" ? (
                        <Button
                          size="sm"
                          variant="primary"
                          className="px-1 py-1 h-9 text-xs"
                          disabled
                          title="Analysis in progress"
                        >
                          <Loader className="h-4 w-4 mr-1 animate-spin" />
                          Analyzing...
                        </Button>
                      ) : application.latestAssessment?.status === "analysis_failed" ? (
                        <Button
                          size="sm"
                          variant="secondary"
                          className="px-1 py-1 h-9 text-xs bg-red-50 text-red-700 border-red-200 hover:bg-red-100"
                          onClick={() => {
                            const retryPath = application.assessmentID
                              ? `/assessment/${application.assessmentID}`
                              : '/assessment';
                            navigate(retryPath, {
                              state: {
                                applicationId: application.applicationId,
                                applicationName: application.applicationName,
                                applicationDescription: application.description,
                                applicationTypeId: application.applicationTypeId,
                                regionId: application.regionId,
                                verticalId: application.verticalId,
                                subVerticalId: application.subVerticalId,
                                userBaseId: application.userBaseId,
                                deploymentModelId: application.deploymentModelId,
                                dealID: selectedDeal.dealID,
                                dealName: selectedDeal.dealName
                              }
                            });
                          }}
                        >
                          <AlertCircle className="h-4 w-4 mr-1" />
                          Retry Analysis
                        </Button>
                      ) : application.hasAssessment && application.assessmentID ? (
                        <Button
                          size="sm"
                          variant="primary"
                          className="px-1 py-1 h-9 text-xs"
                          disabled={application.totalRisks === 0}
                          onClick={() => navigate(`/assessment/${application.assessmentID}/risks`)}
                        >
                          <Shield className="h-4 w-4 mr-1" />
                          View Risks ({application.activeRisks ?? application.totalRisks})
                        </Button>
                      ) : (
                        <Button
                          size="sm"
                          variant="primary"
                          className="px-1 py-1 h-9 text-xs"
                          disabled
                          title="No assessment data available"
                        >
                          <Shield className="h-4 w-4 mr-1" />
                          View Risks (0)
                        </Button>
                      )}
                      {canManageOrganizationRisks() && application.assessmentID && (
                        <Button size="sm" variant="warning" className="px-1 py-1 h-9 text-xs"
                          disabled={application.latestAssessment?.status !== 'completed'}
                          onClick={(e) => {
                            e.stopPropagation();
                            setAssessmentToReassess(application.assessmentID);
                            setShowReassessModal(true);
                          }}>
                          <RefreshCw className="h-4 w-4 mr-1" />
                          Reassess
                        </Button>
                      )}
                      {canDeleteAssessment && application.assessmentID && (
                        <Button size="sm" variant="danger" className="px-1 py-1 h-9 text-xs"
                          onClick={(e) => { e.stopPropagation(); setAssessmentToDelete(application.assessmentID); setShowDeleteModal(true); }}>
                          <Trash2 className="h-4 w-4 mr-1" />
                          Delete
                        </Button>
                      )}
                    </div>

                    {/* Risk details row */}
                    {application.hasAssessment ? (
                      <div className="mt-3">
                        <div className="flex flex-wrap gap-2">
                          {application.riskCounts?.CRITICAL > 0 && (
                            <span className="px-2 py-1 bg-red-100 text-red-800 text-xs rounded">
                              {application.riskCounts.CRITICAL} Critical
                            </span>
                          )}
                          {application.riskCounts?.HIGH > 0 && (
                            <span className="px-2 py-1 bg-orange-100 text-orange-800 text-xs rounded">
                              {application.riskCounts.HIGH} High
                            </span>
                          )}
                          {application.riskCounts?.MEDIUM > 0 && (
                            <span className="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs rounded">
                              {application.riskCounts.MEDIUM} Medium
                            </span>
                          )}
                          {application.riskCounts?.LOW > 0 && (
                            <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded">
                              {application.riskCounts.LOW} Low
                            </span>
                          )}
                        </div>
                        <div className="mt-2">
                          <div className="text-xs text-gray-500 mb-1">
                            Risk Score: {application.riskScore || 0}%
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-4">
                            <div
                              className={`h-4 rounded-full ${(application.riskScore || 0) <= 40
                                ? 'bg-green-500'
                                : (application.riskScore || 0) <= 60
                                  ? 'bg-yellow-500'
                                  : (application.riskScore || 0) <= 80
                                    ? 'bg-orange-500'
                                    : 'bg-red-500'
                                }`}
                              style={{ width: `${application.riskScore || 0}%` }}
                            ></div>
                          </div>
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                          {application.totalRisks || 0} Total Risks
                        </div>
                      </div>
                    ) : (
                      <div className="mt-3 text-xs text-gray-500">No Assessment Data</div>
                    )}

                    {application.assessmentID && (
                      <div className="flex justify-end mt-1">
                        <AssessmentActivityLog assessmentType="deal" assessmentId={String(application.assessmentID)} hideQuestionEvents />
                      </div>
                    )}

                  </div>
                ))}
              </div>
              )
            ) : (
              <div className="text-center py-8">
                <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500">Select a deal to view applications</p>
              </div>
            )}
          </Card>
        </div>
      </div >
      {showAssignModal && selectedApplication && (
        <AssignAssessModal
          isOpen={showAssignModal}
          onClose={() => setShowAssignModal(false)}
          applicationId={selectedApplication.applicationId}
          refreshData={refreshApplications}
          dealId={selectedDeal?.dealID}
          isPrivate={dealDetail?.isPrivate}
        />
      )}
      <AssessmentRespondentsModal
        isOpen={respondentsAssessmentId != null}
        onClose={() => setRespondentsAssessmentId(null)}
        assessmentType="standard"
        assessmentId={respondentsAssessmentId}
        refreshData={refreshApplications}
      />
      <CreateDealModal
        isOpen={showNewDealModal}
        onClose={() => setShowNewDealModal(false)}
        onDealCreated={handleDealCreated}
        dealCategory="Application"
        title="Create New Deal"
      />
      {selectedDeal && (
        <DealShareModal
          isOpen={showShareModal}
          onClose={() => setShowShareModal(false)}
          dealId={selectedDeal.dealID}
          dealName={selectedDeal.dealName}
        />
      )}
      {
        showNewApplicationModal && selectedDeal && (
          <Modal
            isOpen={showNewApplicationModal}
            onClose={() => setShowNewApplicationModal(false)}
            title="New Application"
          >
            <form
              onSubmit={async (e) => {
                e.preventDefault();
                setIsSubmitting(true);
                try {
                  const formFields = new FormData(e.target);
                  const submitData = {
                    applicationName: formFields.get("applicationName"),
                    applicationOwner: formFields.get("applicationOwner"),
                    applicationDescription: formFields.get("applicationDescription"),
                    targetEndDate: formFields.get("targetEndDate")
                      ? new Date(formFields.get("targetEndDate")).toISOString()
                      : null,
                    dealID: selectedDeal.dealID,
                    regionId: parseInt(formFields.get("regionId")),
                    verticalId: parseInt(formFields.get("verticalId")),
                    subVerticalId: parseInt(formFields.get("subVerticalId")),
                    criticality: formFields.get("criticality"),
                    createdBy: 1,
                    applicationTypeId: parseInt(formData.applicationTypeId) || null,
                    userBaseId: parseInt(formData.userBaseId) || null,
                    deploymentModelId: parseInt(formData.deploymentModelId) || null,
                    vendorId: parseInt(formData.vendorId) || null,
                    appScope: formData.appScope || null,
                  };

                  await apiCall("POST", "/applications", submitData);
                  setShowNewApplicationModal(false);
                  fetchApplications(selectedDeal.dealID);
                } catch (error) {
                  const msg = error.response?.data?.message || "Error creating application.";
                  alert(msg);
                } finally {
                  setIsSubmitting(false);
                }
              }}
            >
              {/* Internal / External */}
              <div className="mb-4">
                <label className="block mb-1 text-sm font-medium text-gray-700">Application Scope</label>
                <select
                  value={formData.appScope}
                  onChange={e => setFormData({
                    ...formData,
                    appScope: e.target.value,
                    vendorId: e.target.value !== 'External' ? '' : formData.vendorId,
                  })}
                  className="block w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                >
                  <option value="">-- Select Scope --</option>
                  <option value="Internal">Internal</option>
                  <option value="External">External</option>
                </select>
              </div>

              {/* Vendor Selection */}
              <div className="mb-4">
                <label className="block mb-1 text-sm font-medium text-gray-700">Vendor</label>
                <div className="flex space-x-2">
                  <select
                    name="vendorId"
                    value={formData.vendorId || ""}
                    onChange={e => setFormData({ ...formData, vendorId: e.target.value })}
                    disabled={vendorsLoading || formData.appScope !== 'External'}
                    className="block w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <option value="">-- Select Vendor --</option>
                    {vendors
                      .slice()
                      .sort((a, b) => a.vendorName.localeCompare(b.vendorName))
                      .map(v => (
                        <option key={v.vendorId} value={v.vendorId}>{v.vendorName}</option>
                      ))}
                  </select>
                  <button
                    type="button"
                    onClick={() => setShowNewVendorModal(true)}
                    disabled={formData.appScope !== 'External'}
                    className="px-3 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Add new vendor"
                  >
                    <Plus className="h-4 w-4" />
                  </button>
                </div>
              </div>

              <div className="mb-4">
                <label className="block mb-1 text-sm font-medium text-gray-700">Application Type</label>
                <select
                  name="applicationTypeId"
                  value={formData.applicationTypeId}
                  onChange={e => setFormData({ ...formData, applicationTypeId: e.target.value })}
                  className="block w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                >
                  <option value="">-- Select Application Type --</option>
                  {applicationTypes.map(a => (
                    <option key={a.applicationTypeId} value={a.applicationTypeId}>{a.applicationTypeName}</option>
                  ))}
                </select>
              </div>

              {/* Application Name */}
              <Input label="Application Name" name="applicationName" required />

              <Input label="Application Owner" name="applicationOwner" required />

              {/* User Base */}
              <div className="mb-4">
                <label className="block mb-1 text-sm font-medium text-gray-700">User Base</label>
                <select
                  name="userBaseId"
                  value={formData.userBaseId}
                  onChange={(e) => setFormData({ ...formData, userBaseId: e.target.value })}
                  className="block w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                >
                  <option value="">-- Select User Base --</option>
                  {userBases.map((u) => (
                    <option key={u.userBaseId} value={u.userBaseId}>
                      {u.userBaseTitle}
                    </option>
                  ))}
                </select>
              </div>

              {/* Deployment Model */}
              <div className="mb-4">
                <label className="block mb-1 text-sm font-medium text-gray-700">Deployment Model</label>
                <select
                  name="deploymentModelId"
                  value={formData.deploymentModelId}
                  onChange={e => setFormData({ ...formData, deploymentModelId: e.target.value })}
                  className="block w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                >
                  <option value="">-- Select Deployment Model --</option>
                  {deploymentModels.map(dm => (
                    <option key={dm.deploymentModelId} value={dm.deploymentModelId}>
                      {dm.deploymentModelTitle}
                    </option>
                  ))}
                </select>
              </div>

              {/* Region Select */}
              <RegionField
                regions={regions}
                name="regionId"
                defaultValue={selectedDeal?.regionId}
                label="Data Residency Region"
                className="mb-4"
                labelClassName="block text-sm font-medium text-gray-700 mb-1"
              />

              {/* Vertical Select */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Vertical <span className="text-red-500">*</span>
                </label>
                <select
                  name="verticalId"
                  value={formData.VerticalId}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      VerticalId: parseInt(e.target.value),
                      SubVerticalId: "" // reset sub-vertical
                    })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  required
                >
                  <option value="">-- Select Vertical --</option>
                  {verticals.map((v) => (
                    <option key={v.verticalId} value={v.verticalId}>
                      {v.verticalName}
                    </option>
                  ))}
                </select>
              </div>

              {/* SubVertical Select */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  SubVertical <span className="text-red-500">*</span>
                </label>
                <select
                  name="subVerticalId"
                  value={formData.SubVerticalId}
                  onChange={(e) =>
                    setFormData({ ...formData, SubVerticalId: parseInt(e.target.value) })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md disabled:opacity-50"
                  required
                  disabled={!formData.VerticalId}
                >
                  <option value="">-- Select Sub-Vertical --</option>
                  {verticals
                    .find((v) => v.verticalId === parseInt(formData.VerticalId))
                    ?.subVerticals?.map((sv) => (
                      <option key={sv.subVerticalId} value={sv.subVerticalId}>
                        {sv.subVerticalName}
                      </option>
                    ))}
                </select>
              </div>


              {/* Target End Date */}
              <Input label="Target End Date" name="targetEndDate" type="date" />

              {/* Criticality */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Criticality
                </label>
                <select
                  name="criticality"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                >
                  <option value="LOW">Low</option>
                  <option value="MEDIUM" selected>
                    Medium
                  </option>
                  <option value="HIGH">High</option>
                  <option value="CRITICAL">Critical</option>
                </select>
              </div>
              {/* Description */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  name="applicationDescription"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows="3"
                />
              </div>
              {/* Actions */}
              <div className="flex justify-end space-x-3">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => setShowNewApplicationModal(false)}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={isSubmitting}>
                  {isSubmitting ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 inline" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Creating...
                    </>
                  ) : (
                    "Create Application"
                  )}
                </Button>
              </div>
            </form>
          </Modal>
        )
      }
      {/* New Vendor Modal */}
      <TPRMNewVendorModal
        isOpen={showNewVendorModal}
        onClose={() => setShowNewVendorModal(false)}
        onVendorCreated={handleVendorCreated}
      />
      <DeleteAssessmentModal
        isOpen={showDeleteModal}
        onClose={() => { setShowDeleteModal(false); setAssessmentToDelete(null); }}
        onConfirm={handleDeleteAssessment}
        isDeleting={deleting}
      />
      <ReassessModal
        isOpen={showReassessModal}
        onClose={() => { setShowReassessModal(false); setAssessmentToReassess(null); }}
        onConfirm={handleReassessAssessment}
        isReassessing={reassessing}
      />
      {showEditApplicationModal && editingApplication && (
        <Modal
          isOpen={showEditApplicationModal}
          onClose={() => { setShowEditApplicationModal(false); setEditingApplication(null); }}
          title="Edit Application"
        >
          <form
            onSubmit={async (e) => {
              e.preventDefault();
              setIsSubmitting(true);
              try {
                const submitData = {
                  applicationName: editFormData.applicationName,
                  applicationOwner: editFormData.applicationOwner,
                  applicationDescription: editFormData.applicationDescription,
                  targetEndDate: editFormData.targetEndDate
                    ? new Date(editFormData.targetEndDate).toISOString()
                    : null,
                  criticality: editFormData.criticality,
                  appScope: editFormData.appScope || null,
                  vendorId: parseInt(editFormData.vendorId) || null,
                  verticalId: parseInt(editFormData.verticalId) || null,
                  subVerticalId: parseInt(editFormData.subVerticalId) || null,
                  applicationTypeId: parseInt(editFormData.applicationTypeId) || null,
                  userBaseId: parseInt(editFormData.userBaseId) || null,
                  deploymentModelId: parseInt(editFormData.deploymentModelId) || null,
                };

                await apiCall("PUT", `/applications/${editingApplication.applicationId}`, submitData);
                setShowEditApplicationModal(false);
                setEditingApplication(null);
                fetchApplications(selectedDeal.dealID);
              } catch (error) {
                const msg = error.response?.data?.message || "Error updating application.";
                alert(msg);
              } finally {
                setIsSubmitting(false);
              }
            }}
          >
            {/* Application Scope */}
            <div className="mb-4">
              <label className="block mb-1 text-sm font-medium text-gray-700">Application Scope</label>
              <select
                value={editFormData.appScope}
                onChange={e => setEditFormData({
                  ...editFormData,
                  appScope: e.target.value,
                  vendorId: e.target.value !== 'External' ? '' : editFormData.vendorId,
                })}
                className="block w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              >
                <option value="">-- Select Scope --</option>
                <option value="Internal">Internal</option>
                <option value="External">External</option>
              </select>
            </div>

            {/* Vendor */}
            <div className="mb-4">
              <label className="block mb-1 text-sm font-medium text-gray-700">Vendor</label>
              <select
                value={editFormData.vendorId || ""}
                onChange={e => setEditFormData({ ...editFormData, vendorId: e.target.value })}
                disabled={vendorsLoading || editFormData.appScope !== 'External'}
                className="block w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <option value="">-- Select Vendor --</option>
                {vendors
                  .slice()
                  .sort((a, b) => a.vendorName.localeCompare(b.vendorName))
                  .map(v => (
                    <option key={v.vendorId} value={v.vendorId}>{v.vendorName}</option>
                  ))}
              </select>
            </div>

            {/* Application Type */}
            <div className="mb-4">
              <label className="block mb-1 text-sm font-medium text-gray-700">Application Type</label>
              <select
                value={editFormData.applicationTypeId}
                onChange={e => setEditFormData({ ...editFormData, applicationTypeId: e.target.value })}
                disabled={!editFormData.canEditAll}
                className="block w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <option value="">-- Select Application Type --</option>
                {applicationTypes.map(a => (
                  <option key={a.applicationTypeId} value={a.applicationTypeId}>{a.applicationTypeName}</option>
                ))}
              </select>
            </div>

            {/* Application Name */}
            <div className="mb-4">
              <label className="block mb-1 text-sm font-medium text-gray-700">
                Application Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={editFormData.applicationName}
                onChange={e => setEditFormData({ ...editFormData, applicationName: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            {/* Application Owner */}
            <div className="mb-4">
              <label className="block mb-1 text-sm font-medium text-gray-700">Application Owner</label>
              <input
                type="text"
                value={editFormData.applicationOwner}
                onChange={e => setEditFormData({ ...editFormData, applicationOwner: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* User Base */}
            <div className="mb-4">
              <label className="block mb-1 text-sm font-medium text-gray-700">User Base</label>
              <select
                value={editFormData.userBaseId}
                onChange={e => setEditFormData({ ...editFormData, userBaseId: e.target.value })}
                disabled={!editFormData.canEditAll}
                className="block w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <option value="">-- Select User Base --</option>
                {userBases.map(u => (
                  <option key={u.userBaseId} value={u.userBaseId}>{u.userBaseTitle}</option>
                ))}
              </select>
            </div>

            {/* Deployment Model */}
            <div className="mb-4">
              <label className="block mb-1 text-sm font-medium text-gray-700">Deployment Model</label>
              <select
                value={editFormData.deploymentModelId}
                onChange={e => setEditFormData({ ...editFormData, deploymentModelId: e.target.value })}
                disabled={!editFormData.canEditAll}
                className="block w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <option value="">-- Select Deployment Model --</option>
                {deploymentModels.map(dm => (
                  <option key={dm.deploymentModelId} value={dm.deploymentModelId}>{dm.deploymentModelTitle}</option>
                ))}
              </select>
            </div>

            {/* Region (always disabled) */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Data Residency Region</label>
              <select
                value={editingApplication.regionId}
                disabled
                className="w-full px-3 py-2 border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {regions.map(r => (
                  <option key={r.regionId} value={r.regionId}>{r.regionName}</option>
                ))}
              </select>
            </div>

            {/* Vertical */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Vertical</label>
              <select
                value={editFormData.verticalId}
                onChange={e => setEditFormData({
                  ...editFormData,
                  verticalId: parseInt(e.target.value),
                  subVerticalId: '',
                })}
                disabled={!editFormData.canEditAll}
                className="w-full px-3 py-2 border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <option value="">-- Select Vertical --</option>
                {verticals.map(v => (
                  <option key={v.verticalId} value={v.verticalId}>{v.verticalName}</option>
                ))}
              </select>
            </div>

            {/* SubVertical */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">SubVertical</label>
              <select
                value={editFormData.subVerticalId}
                onChange={e => setEditFormData({ ...editFormData, subVerticalId: parseInt(e.target.value) })}
                disabled={!editFormData.canEditAll || !editFormData.verticalId}
                className="w-full px-3 py-2 border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <option value="">-- Select Sub-Vertical --</option>
                {verticals
                  .find(v => v.verticalId === parseInt(editFormData.verticalId))
                  ?.subVerticals?.map(sv => (
                    <option key={sv.subVerticalId} value={sv.subVerticalId}>{sv.subVerticalName}</option>
                  ))}
              </select>
            </div>

            {/* Target End Date */}
            <div className="mb-4">
              <label className="block mb-1 text-sm font-medium text-gray-700">Target End Date</label>
              <input
                type="date"
                value={editFormData.targetEndDate}
                onChange={e => setEditFormData({ ...editFormData, targetEndDate: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Criticality */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Criticality</label>
              <select
                value={editFormData.criticality}
                onChange={e => setEditFormData({ ...editFormData, criticality: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              >
                <option value="LOW">Low</option>
                <option value="MEDIUM">Medium</option>
                <option value="HIGH">High</option>
                <option value="CRITICAL">Critical</option>
              </select>
            </div>

            {/* Description */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <textarea
                value={editFormData.applicationDescription}
                onChange={e => setEditFormData({ ...editFormData, applicationDescription: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                rows="3"
              />
            </div>

            {!editFormData.canEditAll && (
              <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-md">
                <p className="text-xs text-amber-700">
                  Some fields are locked because an assessment is in progress for this application.
                </p>
              </div>
            )}

            {/* Actions */}
            <div className="flex justify-end space-x-3">
              <Button
                type="button"
                variant="secondary"
                onClick={() => { setShowEditApplicationModal(false); setEditingApplication(null); }}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 inline" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Saving...
                  </>
                ) : (
                  "Save Changes"
                )}
              </Button>
            </div>
          </form>
        </Modal>
      )}
      <AssessmentUnassignedModal
        isOpen={showUnassignedAlert}
        onClose={() => setShowUnassignedAlert(false)}
      />
    </div >
  );
};

export default deal;
